import { MenuItems } from '../enums/MenuItems'
import Section from '../components/Section'
import networkApi from '../api/network/NetworkApi'
import { useCallback, useEffect, useState } from 'react'
import { WorkedWithRelationshipsInfo } from '../api/network/models/WorkedWithRelationshipsInfo'
import personApi from '../api/person/PersonApi'
import companyApi from '../api/company/CompanyApi'
import dataImportApi from '../api/dataImport/DataImportApi'
import { formatDateAllInfo } from '../utils/dateUtils'
import { ImportEventDto } from '../api/dataImport/models/ImportEventDto'
import roleApi from '../api/role/RoleApi'
import LayoutSingleView from '../components/layout/LayoutSingleView'

function DataManagement(): React.JSX.Element {
    const [workedWithRelationshipsInfo, setWorkedWithRelationshipsInfo] =
        useState<WorkedWithRelationshipsInfo | null>(null)
    const [personCount, setPersonCount] = useState<number | null>(null)
    const [companyCount, setCompanyCount] = useState<number | null>(null)
    const [importEventCount, setImportEventCount] = useState<number | null>(
        null,
    )
    const [isLoading, setIsLoading] = useState(false)
    const [lastImportEvent, setLastImportEvent] =
        useState<ImportEventDto | null>(null)
    const [roleId, setRoleId] = useState<string>('ROLol8roiwcgy5pzzr')

    const fetchData = useCallback(async () => {
        try {
            const workedWithRelationshipInfo =
                await networkApi.getWorkedWithRelationshipsInfo()
            setWorkedWithRelationshipsInfo(workedWithRelationshipInfo)

            const personCount = await personApi.getNumberOfPersonNodes()
            setPersonCount(personCount)

            const companyCount = await companyApi.getNumberOfCompanyNodes()
            setCompanyCount(companyCount)

            const importEventCount =
                await dataImportApi.getNumberOfImportEventNodes()
            setImportEventCount(importEventCount)

            if (importEventCount > 0) {
                const lastImportEvent = await dataImportApi.getLastImportEvent()
                setLastImportEvent(lastImportEvent)
            } else {
                setLastImportEvent(null)
            }
        } catch (error) {
            console.error('Error fetching data:', error)
        }
    }, [])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const handleDeleteAllWorkedWithRelationships = async () => {
        if (
            confirm(
                'This will delete ALL the WORKED_WITH relationships.\n\nAre you sure?',
            )
        ) {
            setIsLoading(true)
            try {
                await networkApi.deleteWorkedWithRelationships()
                alert('Deleted all WORKED_WITH relationships')
                await fetchData()
            } catch (error) {
                console.error(error)
                alert('Deleting data failed')
            } finally {
                setIsLoading(false)
            }
        }
    }

    const handleCreateAllWorkedWithRelationships = async () => {
        if (
            confirm(
                'This will create ALL the WORKED_WITH relationships.\n\nAre you sure?',
            )
        ) {
            setIsLoading(true)
            try {
                await networkApi.createWorkedWithRelationships()
                alert('Created all WORKED_WITH relationships')
                await fetchData()
            } catch (error) {
                console.error(error)
                alert('Creating data failed')
            } finally {
                setIsLoading(false)
            }
        }
    }

    const handleProcessRoles = async () => {
        if (!roleId) {
            alert('Please enter a role ID')
            return
        }

        if (
            confirm(
                `This will process the role with ID: ${roleId}.\n\nAre you sure?`,
            )
        ) {
            setIsLoading(true)
            try {
                await roleApi.processRole({
                    roleId,
                })
                alert('Role processing completed successfully')
            } catch (error) {
                console.error('Error processing role:', error)
                alert('Role processing failed')
            } finally {
                setIsLoading(false)
            }
        }
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.DataManagement}>
            <Section title="Data Management Dashboard">
                <div className="mt-6 flex justify-between">
                    <Section subtitle="Summary">
                        <div>
                            Number of Person nodes:{' '}
                            {personCount?.toLocaleString() ?? 'Loading...'}
                        </div>
                        <div>
                            Number of Company nodes:{' '}
                            {companyCount?.toLocaleString() ?? 'Loading...'}
                        </div>
                    </Section>
                    <Section
                        subtitle="WORKED_WITH Relationships"
                        className="flex flex-col"
                    >
                        <div>
                            Existing:{' '}
                            {workedWithRelationshipsInfo
                                ? workedWithRelationshipsInfo.actual.toLocaleString()
                                : 'Loading...'}
                        </div>
                        <div>
                            Expected:{' '}
                            {workedWithRelationshipsInfo
                                ? workedWithRelationshipsInfo.expected.toLocaleString()
                                : 'Loading...'}
                        </div>
                        <div className="mt-2">
                            <button
                                className="danger-button"
                                onClick={handleDeleteAllWorkedWithRelationships}
                                disabled={isLoading}
                            >
                                Delete ALL WORKED_WITH relationships
                            </button>
                        </div>
                        <div className="mt-2">
                            <button
                                className="primary-button"
                                onClick={handleCreateAllWorkedWithRelationships}
                                disabled={isLoading}
                            >
                                Create ALL WORKED_WITH relationships
                            </button>
                        </div>
                    </Section>
                    <Section subtitle="Import Events">
                        <div>
                            Number of Import Event nodes:{' '}
                            {importEventCount?.toLocaleString() ?? 'Loading...'}
                        </div>
                        {importEventCount &&
                        importEventCount > 0 &&
                        lastImportEvent ? (
                            <div className="mt-4 p-4 border rounded bg-gray-100">
                                <h3 className="font-bold">
                                    Last Import Event Details:
                                </h3>
                                <p>
                                    <strong>ID:</strong> {lastImportEvent.id}
                                </p>
                                <p>
                                    <strong>File name:</strong>{' '}
                                    {lastImportEvent.csvFileName}
                                </p>
                                <p>
                                    <strong>Imported By:</strong>{' '}
                                    {lastImportEvent.userName}
                                </p>
                                <p>
                                    <strong>Created At:</strong>{' '}
                                    {formatDateAllInfo(
                                        lastImportEvent.createdAt.toString(),
                                    )}
                                </p>
                                <p>
                                    <strong>Imported people:</strong>{' '}
                                    {lastImportEvent.numberOfImportedPeople}
                                </p>
                            </div>
                        ) : (
                            <div>No import events available.</div>
                        )}
                    </Section>
                    <Section subtitle="Process Roles">
                        <div className="flex flex-col space-y-2">
                            <input
                                type="text"
                                value={roleId}
                                onChange={(e) => setRoleId(e.target.value)}
                                placeholder="Role ID"
                                className="w-full p-2 border border-gray-300 rounded"
                            />
                            <button
                                className="primary-button w-full"
                                onClick={handleProcessRoles}
                                disabled={isLoading}
                            >
                                Process Roles
                            </button>
                        </div>
                    </Section>
                </div>
            </Section>
        </LayoutSingleView>
    )
}

export default DataManagement
